/**
 * Human label for a model id, for the little "which model wrote this" tag under
 * an assistant reply.
 *
 * Deliberately a PURE string transform with no imports. The obvious alternative
 * — read `MERIDIAN_MODEL_REGISTRY` — drags the provider/env and capability
 * modules into the client bundle for what is a cosmetic caption, and it goes
 * blank for exactly the ids worth seeing: the env-configured model that is not
 * in the picker, or one that has since been retired from it. Falling back to the
 * raw id is strictly better than rendering nothing.
 */

/** Vendor prefixes we print with real capitalization. */
const VENDOR_LABELS: ReadonlyMap<string, string> = new Map([
  ['claude', 'Claude'],
  ['deepseek', 'DeepSeek'],
]);

/** `claude-sonnet-4-6` → `Claude Sonnet 4.6`; `deepseek-v4-flash` → `DeepSeek V4 Flash`. */
export function formatModelLabel(modelId: string | null | undefined): string | null {
  const id = (modelId ?? '').trim();
  if (!id) return null;

  // Drop a trailing snapshot date (`-20251001`): it is noise in a caption, and
  // the tooltip carries the exact id anyway.
  const withoutSnapshot = id.replace(/-\d{8}$/, '');
  const segments = withoutSnapshot.split('-').filter(Boolean);
  if (segments.length === 0) return id;

  const [vendor, ...rest] = segments;
  const vendorLabel = VENDOR_LABELS.get(vendor.toLowerCase());
  if (!vendorLabel) return id;

  // Trailing numeric segments are a version: 4, 6 → "4.6".
  const versionParts: string[] = [];
  while (rest.length > 0 && /^\d+$/.test(rest[rest.length - 1]!)) {
    versionParts.unshift(rest.pop()!);
  }

  const words = rest.map((word) =>
    // `v4` → `V4`, `sonnet` → `Sonnet`.
    word.charAt(0).toUpperCase() + word.slice(1),
  );
  const version = versionParts.join('.');

  return [vendorLabel, ...words, version].filter(Boolean).join(' ');
}

/** Shape `loadChat` puts on an assistant message's `metadata`. */
export interface MessageModelAttribution {
  readonly modelId?: unknown;
  readonly provider?: unknown;
}

/**
 * Read the model id off a message's metadata, or `null` when the message
 * predates attribution (every row written before 2026-08-29) or is a user
 * message. Tolerant of the `unknown`-typed metadata bag by design — a stray
 * value must render nothing, never throw inside a message list.
 */
export function readMessageModelId(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const { modelId } = metadata as MessageModelAttribution;
  return typeof modelId === 'string' && modelId.trim().length > 0 ? modelId.trim() : null;
}
